/**
 * Scroll entrances.
 *
 * One shared IntersectionObserver for the whole page instead of one per
 * component, which is what the old Benefits.vue did. Elements reveal once and
 * are unobserved immediately afterwards.
 *
 * The composable returns a ref rather than adding a class itself. Vue patches
 * `class` by replacing it wholesale, so a class added imperatively is silently
 * wiped the next time the component re-renders — which is exactly how a
 * revealed section can end up invisible again. Binding a ref keeps Vue the
 * single source of truth.
 *
 * Usage:
 *   const el = ref(null)
 *   const { revealed } = useScrollReveal(el)
 *   <div ref="el" class="reveal" :class="{ 'is-revealed': revealed }">
 */
import { ref, onMounted, onBeforeUnmount, unref } from "vue";

export function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

let observer = null;
const callbacks = new WeakMap();

function getObserver() {
  if (observer || typeof window === "undefined") return observer;

  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        callbacks.get(entry.target)?.();
        callbacks.delete(entry.target);
      });
    },
    // Fire a little before the element is fully on screen.
    { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
  );

  return observer;
}

/**
 * @param {import('vue').Ref<HTMLElement>|HTMLElement} target
 * @param {object} [options]
 * @param {Function} [options.onReveal] called once, when the element reveals
 * @returns {{ revealed: import('vue').Ref<boolean> }}
 */
export function useScrollReveal(target, options = {}) {
  const revealed = ref(false);
  let el = null;

  const reveal = () => {
    revealed.value = true;
    options.onReveal?.();
  };

  onMounted(() => {
    el = unref(target);
    if (!el) return;

    if (prefersReducedMotion() || !("IntersectionObserver" in window)) {
      reveal();
      return;
    }

    callbacks.set(el, reveal);
    getObserver()?.observe(el);
  });

  onBeforeUnmount(() => {
    if (el && observer) {
      observer.unobserve(el);
      callbacks.delete(el);
    }
  });

  return { revealed };
}
